import { UserCreateInput } from 'dtos/users/UserCreateInput';
import { makeAutoObservable, runInAction } from 'mobx';
import { AuthStore } from 'stores/users/AuthStore';
import { createForm } from 'forms/createUserForm';
import { Role, emailAlreadyExistsMessage } from 'dtos/users/common';
import { Match } from 'navigo';
import { LoadStatus } from 'utils/types';
import { serverUri } from 'utils/config';
import { nullsToUndefined } from 'utils/nullsToUndefined';

export class CreateUserStore {
  form = createForm();
  status: LoadStatus = 'none';
  message?: string;

  constructor(private readonly authStore: AuthStore) {
    this.authStore = authStore;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  activate(match: Match) {
    this.status = 'none';
    this.message = undefined;
    this.form.reset();
  }

  async createUser() {
    this.status = 'loading';
    this.message = undefined;
    const { isValid } = await this.form.validate({ showErrors: true });
    if (!isValid) {
      return;
    }
    const dto = this.formToDto(this.form.values());
    try {
      const response = await this.authStore.fetchWithAuth(`${serverUri}/admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dto),
      });
      if (!response.ok) {
        const json = nullsToUndefined(await response.json());
        runInAction(() => {
          this.status = 'error';
          if (json && json.message === emailAlreadyExistsMessage) {
            this.message = 'Email address is already in use';
          } else {
            this.message = `Unable to create user.${json.message ? ` ${json.message}` : ''}`;
          }
        });
        return;
      }
      this.form.reset();
      runInAction(() => {
        this.status = 'success';
        this.message = 'The user was created.';
      });
    } catch (err) {
      runInAction(() => {
        this.status = 'error';
        this.message = `Unable to create user.${err instanceof Error ? ` ${err.message}` : ''}`;
      });
    }
  }

  private formToDto(form: any): UserCreateInput {
    const { username, password, email, enabled, internal, admin } = form;
    const editedRoles: Role[] = [];
    if (enabled) editedRoles.push(Role.USER);
    if (internal) editedRoles.push(Role.INTERNAL);
    if (admin) editedRoles.push(Role.ADMIN);
    const dto: UserCreateInput = {
      username,
      password,
      email,
      roles: editedRoles,
    };
    return dto;
  }
}
